import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import type {
  AutopilotJournalSeverity,
  JournalVenueFilter,
  MergedJournalFilter,
  MergedJournalItem,
  MergedJournalPage,
} from "@auto-pools/core";
import { api } from "../api/client";
import { dexName, dexPoolUrl } from "../lib/dex";
import { formatClockTime, formatJournalDay, journalDayKey } from "../lib/format";
import { useAutopilot } from "./autopilotContext";

/*
 * The one journal of both engines (design 55, plan Phase 5): `GET /api/journal?venue=…&filter=…&
 * before=<cursor>`. The four tabs are a single mutually-exclusive choice over two independent
 * server dimensions — "Decisions only" never combines with a venue pick in this UI, exactly as
 * design 55 draws it (one flat row of four, not a 2-axis picker).
 */
type JournalTab = "all" | "pools" | "spot" | "decisions";
const TABS: { tab: JournalTab; label: string }[] = [
  { tab: "all", label: "Everything" },
  { tab: "pools", label: "Pools" },
  { tab: "spot", label: "Spot" },
  { tab: "decisions", label: "Decisions only" },
];
function tabQuery(tab: JournalTab): { venue: JournalVenueFilter; filter: MergedJournalFilter } {
  switch (tab) {
    case "all":
      return { venue: "all", filter: "all" };
    case "pools":
      return { venue: "pools", filter: "all" };
    case "spot":
      return { venue: "spot", filter: "all" };
    case "decisions":
      return { venue: "all", filter: "decisions" };
  }
}

const SEVERITY_TONE: Record<AutopilotJournalSeverity, string> = {
  info: "good",
  warning: "warn",
  urgent: "bad",
};
/**
 * The venue badge names a pools line's DEX (design 60). A pools line without a pool (pause, resume,
 * limits) is about the whole pools engine, so it says "Pools" rather than claiming a DEX.
 */
function venueLabel(item: MergedJournalItem) {
  if (item.venue === "spot") return "Bybit";
  return item.entry.poolAddress || item.entry.pair ? dexName(item.entry.dex) : "Pools";
}

function JournalRow({
  item,
  onOpen,
}: {
  item: MergedJournalItem;
  onOpen: (target: { operationId?: string; positionId?: string }) => void;
}) {
  const tone = SEVERITY_TONE[item.severity];
  const link =
    item.venue === "pools"
      ? item.entry.operationId
        ? { label: "Operation →", target: { operationId: item.entry.operationId } }
        : item.entry.positionId
          ? { label: "Position →", target: { positionId: item.entry.positionId } }
          : item.entry.poolAddress
            ? { label: "Pool ↗", href: dexPoolUrl(item.entry.dex, item.entry.poolAddress) }
            : null
      : null;
  const pair = item.venue === "pools" ? item.entry.pair : null;
  return (
    <li className={`autopilot-journal-row${item.muted ? " muted" : ""}`}>
      <span className="num autopilot-journal-time">{formatClockTime(item.at)}</span>
      <span className={`autopilot-journal-venue ${item.venue === "spot" ? "spot" : ""}`}>
        {venueLabel(item)}
      </span>
      <span className={`status-dot ${tone}`} aria-hidden="true" />
      <span className="autopilot-journal-text">
        {item.text}
        {pair && <span className="autopilot-journal-pair"> · {pair}</span>}
        {item.detail && <small className="autopilot-journal-detail">{item.detail}</small>}
      </span>
      {link &&
        ("href" in link ? (
          <a className="pool-link" href={link.href} target="_blank" rel="noreferrer">
            {link.label}
          </a>
        ) : (
          <button
            type="button"
            className="button ghost small"
            onClick={() => {
              onOpen(link.target);
            }}
          >
            {link.label}
          </button>
        ))}
    </li>
  );
}

/** Interleaves a day-group header `<li>` before the first row of each new local calendar day. */
function JournalRows({
  items,
  onOpen,
}: {
  items: MergedJournalItem[];
  onOpen: (target: { operationId?: string; positionId?: string }) => void;
}) {
  let lastDay: number | null = null;
  return (
    <>
      {items.map((item) => {
        const day = journalDayKey(item.at);
        const header = day !== lastDay;
        lastDay = day;
        return (
          <Fragment key={item.cursor}>
            {header && <li className="autopilot-journal-day">{formatJournalDay(item.at)}</li>}
            <JournalRow item={item} onOpen={onOpen} />
          </Fragment>
        );
      })}
    </>
  );
}

export function AutopilotJournal({
  onOpen,
}: {
  onOpen: (target: { operationId?: string; positionId?: string }) => void;
}) {
  const autopilot = useAutopilot();
  const [tab, setTab] = useState<JournalTab>("all");
  const [page, setPage] = useState<MergedJournalPage>({ items: [], nextBefore: null });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Shared across loadFirstPage and loadEarlier (D5): every call — a tab switch, the periodic
  // page-1 refresh, or a "Load earlier" click — bumps this, and a response is applied only while
  // it is still the most recently issued request. A response that loses the race (a slower request
  // that started earlier) is simply dropped instead of overwriting a newer, already-applied result.
  const requestSeq = useRef(0);

  const loadFirstPage = useCallback(async (activeTab: JournalTab) => {
    const { venue, filter } = tabQuery(activeTab);
    const requestId = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const fresh = await api<MergedJournalPage>(`/journal?venue=${venue}&filter=${filter}`, {
        cache: "no-store",
      });
      if (requestSeq.current !== requestId) return;
      setPage((previous) => {
        // A live refresh only replaces page 1: earlier entries already loaded via "Load earlier"
        // stay attached below it, so an operator reading the older feed never sees it reset or
        // gain a duplicate/gap from merging pages by hand. `cursor` is opaque (not numerically
        // orderable across the two engines' interleaved rows), so overlap is membership, not `<=`.
        if (fresh.items.length === 0) return previous;
        if (previous.items.length === 0)
          return { items: fresh.items, nextBefore: fresh.nextBefore };
        const freshCursors = new Set(fresh.items.map((item) => item.cursor));
        const overlaps = freshCursors.has(previous.items[0].cursor);
        // The fresh page reaches back far enough to reconnect with what's already loaded exactly
        // when the previously-newest row's cursor shows up somewhere inside it; otherwise more rows
        // arrived than this page holds, opening a real gap (D4) — drop the stale older rows rather
        // than show a permanent hole, and continue from the fresh cursor.
        if (!overlaps) return { items: fresh.items, nextBefore: fresh.nextBefore };
        const remainder = previous.items.filter((item) => !freshCursors.has(item.cursor));
        return {
          items: [...fresh.items, ...remainder],
          nextBefore: remainder.length > 0 ? previous.nextBefore : fresh.nextBefore,
        };
      });
    } catch (reason) {
      if (requestSeq.current !== requestId) return;
      setError(reason instanceof Error ? reason.message : "Journal unavailable");
    } finally {
      if (requestSeq.current === requestId) setLoading(false);
    }
  }, []);

  const autopilotView = autopilot.data;
  const hasSpot = Boolean(autopilotView?.spot);
  useEffect(() => {
    setPage({ items: [], nextBefore: null });
    void loadFirstPage(tab);
  }, [tab, loadFirstPage]);
  useEffect(() => {
    // Re-fetches page 1 whenever the shared autopilot view changes (its own 15 s poll or an SSE
    // "autopilot"/"spot" tick), merged onto whatever was already loaded (see `loadFirstPage`).
    if (autopilotView) void loadFirstPage(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tab changes are handled above.
  }, [autopilotView]);

  async function loadEarlier() {
    if (page.nextBefore === null || loadingMore) return;
    const { venue, filter } = tabQuery(tab);
    const requestId = ++requestSeq.current;
    setLoadingMore(true);
    setError(null);
    try {
      const older = await api<MergedJournalPage>(
        `/journal?venue=${venue}&filter=${filter}&before=${page.nextBefore}`,
        { cache: "no-store" },
      );
      if (requestSeq.current !== requestId) return;
      setPage((previous) => ({
        items: [...previous.items, ...older.items],
        nextBefore: older.nextBefore,
      }));
    } catch (reason) {
      if (requestSeq.current !== requestId) return;
      setError(reason instanceof Error ? reason.message : "Could not load earlier entries");
    } finally {
      // Unlike `loading` in loadFirstPage — where a still-newer loadFirstPage call is always the
      // one that eventually settles and clears it — nothing ever reissues a "superseded" loadEarlier
      // to reclaim and clear this flag: the button disables itself via `loadingMore` while pending,
      // so at most one loadEarlier call is ever in flight. A superseding loadFirstPage (a tab switch
      // or a live refresh) still legitimately owns the page content above, but this request must
      // release its own pending flag regardless of who won, or "Load earlier" would stay disabled
      // forever (review D8).
      setLoadingMore(false);
    }
  }

  const visibleTabs = TABS.filter(
    ({ tab: value }) => hasSpot || (value !== "pools" && value !== "spot"),
  );

  return (
    <section className="panel autopilot-journal" aria-label="Autopilot journal">
      <div className="autopilot-journal-toolbar">
        <div role="tablist" aria-label="Journal filter" className="history-tabs">
          {visibleTabs.map(({ tab: value, label }) => (
            <button
              key={value}
              role="tab"
              aria-selected={tab === value}
              onClick={() => {
                setTab(value);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {error && (
        <p className="inline-alert warning" role="alert">
          {error}
        </p>
      )}
      {loading && page.items.length === 0 ? (
        <p role="status">Loading journal…</p>
      ) : page.items.length === 0 ? (
        <p className="autopilot-journal-empty">No entries yet.</p>
      ) : (
        <ul className="autopilot-journal-list">
          <JournalRows items={page.items} onOpen={onOpen} />
        </ul>
      )}
      {page.nextBefore !== null && (
        <button
          type="button"
          className="button ghost"
          disabled={loadingMore}
          onClick={() => void loadEarlier()}
        >
          {loadingMore ? "Loading…" : "Load earlier"}
        </button>
      )}
    </section>
  );
}
